import { Link } from '@tanstack/react-router';
import styled from 'styled-components';

interface TutorialCardProps {
  slug: string;
  title: string;
  date: string;
  description: string;
  imageUrl?: string;
  basePath?: string;
}

export function ArticleCard({
  slug,
  title,
  date,
  description,
  imageUrl,
  basePath = '/study-helper-tutorials',
}: TutorialCardProps) {
  return (
    <Card to={`${basePath}/${slug}`}>
      {imageUrl && (
        <ImageContainer>
          <Image src={imageUrl} alt={title} loading="lazy" />
        </ImageContainer>
      )}
      <Content $hasImage={!!imageUrl}>
        <Header>
          <Title>{title}</Title>
          <DateText>{date}</DateText>
        </Header>
        <Description>{description}</Description>
      </Content>
    </Card>
  );
}

const Card = styled(Link)`
  background: var(--white);
  border: 1px solid var(--grey-300);
  border-radius: 5px;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ImageContainer = styled.div`
  background: var(--grey-100);
  height: 233px;
  overflow: hidden;
  width: 100%;
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease-in-out;
  width: 100%;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const Content = styled.div<{ $hasImage: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 10px 24px 63px 20px;
`;

const Header = styled.div`
  margin-bottom: 16px;
  position: relative;
`;

const Title = styled.h3`
  color: var(--grey-900);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease-in-out;
  white-space: nowrap;

  ${Card}:hover & {
    color: var(--wbs-red);
  }
`;

const DateText = styled.div`
  color: var(--grey-500);
  font-size: 12px;
  padding-bottom: 19px;
  position: relative;

  &::after {
    background-color: var(--wbs-red);
    bottom: 0;
    content: '';
    height: 4px;
    left: 0;
    position: absolute;
    width: 40px;
  }
`;

const Description = styled.p`
  color: var(--grey-600);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;
